// Considered: AddMetaspaceDialogOrganism — rejected because this promotes an existing fork rather than picking from a catalog; form fields (name, system_name, description) are structurally different. DialogWrapper (Reuse Audit: "All new dialogs") is composed internally. See spec Reuse Audit.
import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'localization';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import './PromoteMetaspaceDialogOrganism.scss';

export interface PromoteMetaspaceDialogOrganismProps {
  open: boolean;
  metaspaceId: string;
  currentName?: string;
  onClose: () => void;
  onPromoted: () => void;
}

const slugify = (str: string): string =>
  str
    .toLowerCase()
    .replaceAll(/[^a-z0-9\s_]/g, '')
    .trim()
    .replaceAll(/\s+/g, '_');

const PromoteMetaspaceDialogOrganism: React.FC<PromoteMetaspaceDialogOrganismProps> = ({
  open,
  metaspaceId,
  currentName = '',
  onClose,
  onPromoted,
}) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();

  const [name, setName] = useState(currentName);
  const [systemName, setSystemName] = useState(slugify(currentName));
  const [systemNameTouched, setSystemNameTouched] = useState(false);
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName(currentName);
      setSystemName(slugify(currentName));
      setSystemNameTouched(false);
      setDescription('');
      setError(null);
      setSubmitting(false);
    }
  }, [open, currentName]);

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      setName(val);
      if (!systemNameTouched) {
        setSystemName(slugify(val));
      }
    },
    [systemNameTouched]
  );

  const handleSystemNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSystemName(e.target.value);
      setSystemNameTouched(true);
    },
    []
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!apiClients?.schemaClient || !name.trim() || !systemName.trim()) return;

      setSubmitting(true);
      setError(null);

      try {
        await apiClients.schemaClient.post(`/metaspaces/${encodeURIComponent(metaspaceId)}/promote`, {
          name: name.trim(),
          systemName: systemName.trim(),
          description: description.trim() || undefined,
        });
        onPromoted();
        onClose();
      } catch {
        setError(t('sphereMetaspaces.promote.error'));
      } finally {
        setSubmitting(false);
      }
    },
    [apiClients, metaspaceId, name, systemName, description, onPromoted, onClose, t]
  );

  const handleClose = useCallback(() => {
    if (submitting) return;
    onClose();
  }, [submitting, onClose]);

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) handleClose();
    },
    [handleClose]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    },
    [handleClose]
  );

  if (!open) return null;

  return (
    <div
      className="marketplace-dialog-overlay"
      onClick={handleOverlayClick}
      onKeyDown={handleKeyDown}
    >
      <div
        className="marketplace-dialog marketplace-promote-metaspace-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="promote-metaspace-dialog-title"
      >
        <div className="marketplace-dialog__header">
          <h2 id="promote-metaspace-dialog-title" className="marketplace-dialog__title">
            {t('sphereMetaspaces.promote.title')}
          </h2>
          <button
            type="button"
            className="marketplace-dialog__close-btn"
            onClick={handleClose}
            aria-label={t('common.actions.close')}
            disabled={submitting}
          >
            &times;
          </button>
        </div>

        <form className="marketplace-dialog__body" onSubmit={handleSubmit}>
          <div className="marketplace-promote-metaspace-dialog__fields">
            <div className="marketplace-dialog__field">
              <label className="marketplace-dialog__label">
                {t('sphereMetaspaces.promote.nameLabel')}
                <span className="marketplace-dialog__required"> *</span>
              </label>
              <input
                type="text"
                className="marketplace-dialog__input"
                placeholder={t('sphereMetaspaces.promote.namePlaceholder')}
                value={name}
                onChange={handleNameChange}
                required
                autoFocus
              />
            </div>

            <div className="marketplace-dialog__field">
              <label className="marketplace-dialog__label">
                {t('sphereMetaspaces.promote.systemNameLabel')}
                <span className="marketplace-dialog__required"> *</span>
              </label>
              <input
                type="text"
                className="marketplace-dialog__input"
                placeholder={t('sphereMetaspaces.promote.systemNamePlaceholder')}
                value={systemName}
                onChange={handleSystemNameChange}
                required
                pattern="[a-z0-9_]+"
              />
            </div>

            <div className="marketplace-dialog__field">
              <label className="marketplace-dialog__label">
                {t('sphereMetaspaces.promote.descriptionLabel')}
              </label>
              <textarea
                className="marketplace-dialog__textarea"
                placeholder={t('sphereMetaspaces.promote.descriptionPlaceholder')}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
              />
            </div>
          </div>

          {error && <div className="marketplace-dialog__error">{error}</div>}

          <div className="marketplace-dialog__actions">
            <button
              type="button"
              className="marketplace-dialog__cancel-btn"
              onClick={handleClose}
              disabled={submitting}
            >
              {t('sphereMetaspaces.promote.cancel')}
            </button>
            <button
              type="submit"
              className="marketplace-dialog__submit-btn"
              disabled={!name.trim() || !systemName.trim() || submitting}
            >
              {submitting
                ? t('sphereMetaspaces.promote.promoting')
                : t('sphereMetaspaces.promote.confirm')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

PromoteMetaspaceDialogOrganism.displayName = 'PromoteMetaspaceDialogOrganism';

export default PromoteMetaspaceDialogOrganism;
